import React from 'react';
import { Check, Circle, Clock, AlertCircle, Trash2, User } from 'lucide-react';

const PRIORITY_TONE = {
  urgent: 'bg-red-500/15 text-red-300 border-red-500/25',
  high:   'bg-amber-500/15 text-amber-300 border-amber-500/25',
  normal: 'bg-white/[0.04] text-gray-400 border-white/[0.08]',
  low:    'bg-white/[0.03] text-gray-500 border-white/[0.06]',
};

const STATUS_TONE = {
  todo:        { icon: Circle, cls: 'text-gray-500' },
  in_progress: { icon: Clock, cls: 'text-cyan-300' },
  blocked:     { icon: AlertCircle, cls: 'text-amber-300' },
  done:        { icon: Check, cls: 'text-emerald-300' },
  canceled:    { icon: Circle, cls: 'text-gray-600' },
};

export default function TaskRow({ task, onComplete, onDelete, showAccount = false }) {
  if (!task) return null;
  const isDone = task.status === 'done';
  const StatusIcon = (STATUS_TONE[task.status] || STATUS_TONE.todo).icon;
  const statusCls = (STATUS_TONE[task.status] || STATUS_TONE.todo).cls;
  return (
    <div className={`flex items-center gap-3 py-2.5 px-3 rounded-lg border border-white/[0.04] bg-white/[0.01] hover:bg-white/[0.025] transition-colors ${isDone ? 'opacity-60' : ''}`}>
      <button
        onClick={() => onComplete?.(task)}
        className={`shrink-0 w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${isDone ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300' : 'border-white/[0.15] text-gray-500 hover:border-white/[0.3]'}`}
        aria-label={isDone ? 'Completed' : 'Mark complete'}
        disabled={isDone || !onComplete}
      >
        {isDone && <Check className="w-3 h-3" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className={`text-[13px] ${isDone ? 'line-through text-gray-500' : 'text-white'}`}>{task.title}</div>
        <div className="flex items-center gap-x-3 gap-y-0.5 flex-wrap text-[11px] text-gray-500 mt-0.5">
          <span className={`inline-flex items-center gap-1 ${statusCls}`}>
            <StatusIcon className="w-3 h-3" /> {task.status.replace('_', ' ')}
          </span>
          {task.assigneeName && (
            <span className="inline-flex items-center gap-1 text-gray-400">
              <User className="w-3 h-3" /> {task.assigneeName}
            </span>
          )}
          {task.dueDate && <span>due {task.dueDate}</span>}
          {showAccount && task.accountId && <span>#{String(task.accountId).slice(0, 8)}</span>}
        </div>
      </div>
      {task.priority && task.priority !== 'normal' && (
        <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-medium border ${PRIORITY_TONE[task.priority] || PRIORITY_TONE.normal}`}>
          {task.priority}
        </span>
      )}
      {onDelete && (
        <button
          onClick={() => onDelete(task)}
          className="p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10"
          aria-label="Delete task"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
